import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { UserCog } from 'lucide-react'
import { useAuthStore } from '../store/authStore'
import { fetchPermissions } from '../api/auth'
import { roleNameOf } from '../utils/roles'

/**
 * Globální pruh režimu „Přihlásit se jako jiný uživatel". Vykresluje se nad celou aplikací, jen když
 * běží impersonace. Svou výšku publikuje do CSS proměnné `--impersonation-bar`, aby si ji layouty
 * s výškou na celou obrazovku (AppLayout, třídy h-app / min-h-app) mohly odečíst.
 */
export default function ImpersonationBanner() {
  const user = useAuthStore((s) => s.user)
  const impersonator = useAuthStore((s) => s.impersonator)
  const stopImpersonation = useAuthStore((s) => s.stopImpersonation)
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const barRef = useRef<HTMLDivElement>(null)
  const [stopping, setStopping] = useState(false)

  const active = impersonator !== null && user !== null

  useLayoutEffect(() => {
    const root = document.documentElement
    const el = barRef.current
    if (!active || !el) {
      root.style.setProperty('--impersonation-bar', '0px')
      return
    }
    const update = () => root.style.setProperty('--impersonation-bar', `${el.offsetHeight}px`)
    update()
    const observer = new ResizeObserver(update)
    observer.observe(el)
    return () => {
      observer.disconnect()
      root.style.setProperty('--impersonation-bar', '0px')
    }
  }, [active])

  // Po návratu k adminovi resetovat stav tlačítka pro případnou další impersonaci.
  useEffect(() => {
    if (!active) setStopping(false)
  }, [active])

  if (!active) return null

  const handleStop = async () => {
    setStopping(true)
    stopImpersonation()
    queryClient.clear()
    await fetchPermissions()
    navigate('/admin/users')
  }

  return (
    <div
      ref={barRef}
      className="sticky top-0 z-[45] border-b border-amber-300 bg-amber-50 px-4 py-2 flex flex-col sm:flex-row sm:items-center justify-between gap-2"
    >
      <div className="flex items-start sm:items-center gap-2 min-w-0">
        <UserCog size={18} className="text-amber-700 flex-shrink-0 mt-0.5 sm:mt-0" />
        <div className="text-sm text-amber-900">
          Pracujete jako <strong>{user.name}</strong> ({roleNameOf(user)}) — změny se ukládají jeho jménem,
          v historii změn je ale vidět, že je provedl {impersonator.user.name}.
        </div>
      </div>
      <button
        onClick={handleStop}
        disabled={stopping}
        className="self-end sm:self-auto flex-shrink-0 min-h-[40px] px-3 text-sm font-semibold text-amber-900 bg-white border border-amber-300 rounded-lg hover:bg-amber-100 disabled:opacity-50"
      >
        Ukončit režim
      </button>
    </div>
  )
}
